import { useEffect } from 'react';
import useSWR, { mutate } from 'swr';

import { fetchEndpointData } from '@/utils/fetch.client';
import { WebSocketConnection, wsUrl } from '@/utils/websocket';

export type AgentSessionStatus = 'planning' | 'provisioning' | 'running' | 'hibernated' | 'error';

/**
 * Which agent a session runs. A `plan` session has no sandbox at all — it reads the repository
 * read-only and drafts a plan — and only becomes `code` (provisioning a sandbox for the first
 * time) when the user starts coding from the finished plan.
 */
export type AgentSessionMode = 'plan' | 'code';

/** Mirrors `db_client::models::SandboxStatus`. */
export type SandboxStatus = 'none' | 'provisioning' | 'ready' | 'stopping' | 'stopped' | 'archived' | 'error';

/**
 * Stages the backend writes while bringing a sandbox up. Cold starts write
 * `snapshot`/`create`/…; the resume path writes `resume_*`/`fast_forward`/
 * `inject_key`, which is how the UI tells a resume from a first boot.
 * `sandbox_started` is written by the Daytona webhook when the VM reports up.
 */
export type ProvisionStep =
  | 'snapshot'
  | 'create'
  | 'sandbox_started'
  | 'health_check'
  | 'clone'
  | 'setup_commands'
  | 'resume_start'
  | 'fast_forward'
  | 'inject_key';

/** Mirrors the backend `AgentSession` model (`db-client::models::AgentSession`). */
export interface AgentSession {
  id: string;
  team_id: string;
  repository_id: string;
  user_id: string;
  name: string | null;
  branch: string | null;
  status: AgentSessionStatus;
  sandbox_id: string | null;
  sandbox_status: SandboxStatus;
  sandbox_error_message: string | null;
  provision_step: ProvisionStep | null;
  initial_prompt: string | null;
  /** Base64 data URLs attached to `initial_prompt`, delivered to the agent with it. */
  initial_images: string[] | null;
  tracker_issue_id: string | null;
  snapshot_override: string | null;
  /**
   * When the bound agent loop last emitted anything, or `null` once it ended.
   *
   * The only field that answers "is the agent working *right now*". `status` is the sandbox and
   * is written once when the session goes live; `last_turn_at` only moves when a turn *completes*,
   * so neither of them moves during a long run. The server stamps this from the agent's own event
   * stream (throttled to ~10s) and clears it when the loop exits — see `routes/sandbox/agent.rs`.
   * Present on both the list and single-session endpoints.
   */
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
  initial_prompt_delivered_at: string | null;
  /** Set when the user archived the session; archived sessions are hidden from the default view. */
  archived_at: string | null;
  /** Hard cap on this session's model spend, in micro-USD. `null` = uncapped. */
  budget_limit_micro_usd: number | null;
  /** When the cap was first reached; the session's model calls are refused from then on. */
  budget_exhausted_at: string | null;
  /** Running total of the session's model spend, in micro-USD. */
  spent_micro_usd: number;
  /** Branch `branch` was created off; the base for review diffs and pull requests. */
  base_branch: string | null;
  /** Client alias of the Fireworks model the session runs on; `null` uses the default. */
  model: string | null;
  /** Whether this session is planning (no sandbox) or coding. */
  mode: AgentSessionMode;
  /**
   * The highest turn `seq` when the session handed off from planning to coding. The coding agent
   * is only given turns above it; the thread still renders all of them.
   */
  plan_handoff_seq: number | null;
  /**
   * When the agent stopped inside an `ask_user` call, waiting on a human.
   *
   * AGENT state, not sandbox state, and the only field that can express it. The sandbox may be
   * running, hibernated, or gone; `last_turn_role` reads `'assistant'` either way, because an
   * agent that stopped to ask has just written an assistant turn exactly like one that simply
   * finished replying. Present on both the list and single-session endpoints.
   */
  awaiting_user_input_at: string | null;
  /**
   * Which side spoke last — `'user'` while the agent is still working on the latest prompt,
   * `'assistant'` once it has answered and is waiting on a human.
   *
   * Only the **list** endpoint derives this (see `routes/agent_sessions/list.rs`); on a single
   * session it is absent, because that view reads the turns themselves.
   */
  last_turn_role?: 'user' | 'assistant' | null;
  last_turn_at?: string | null;
  /**
   * Whether the backend polls this session's pull request and may wake the agent
   * to fix conflicts, CI failures and reviews. On by default; the watcher turns it
   * off for good when someone pushes to the branch from outside the session.
   */
  pr_watch_enabled: boolean;
  /**
   * Why watching stopped — `external_commit`, `wake_limit`, `budget`, `pr_merged`,
   * `pr_closed`, `token_invalid`, `provider_error`, `user`. `null` while watching.
   */
  pr_watch_stopped_reason: string | null;
}

/** Mirrors `SessionUsageResponse` (`routes/agent_sessions/budget.rs`). */
export interface AgentSessionUsage {
  session_id: string;
  limit_micro_usd: number | null;
  spent_micro_usd: number;
  /** `null` when uncapped; never negative. */
  remaining_micro_usd: number | null;
  request_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  cached_prompt_tokens: number;
  /**
   * Requests upstream never reported token counts for. They bill as zero, so
   * `spent_micro_usd` is a lower bound whenever this is non-zero.
   */
  unpriced_request_count: number;
  /**
   * Billing corrections appended against this session's charges, and their net
   * effect (negative for reversals). The ledger is append-only, so an adjusted
   * charge shows up as an extra row rather than a changed one — this is what
   * explains a spend figure below the sum of the requests listed alongside it.
   */
  correction_count: number;
  corrections_micro_usd: number;
  exhausted: boolean;
  exhausted_at: string | null;
}

export const agentSessionsKey = (teamId: string) => `/api/v1/agent-sessions?team_id=${encodeURIComponent(teamId)}`;
export const agentSessionKey = (sessionId: string) => `/api/v1/agent-sessions/${sessionId}`;
export const agentSessionTurnsKey = (sessionId: string) => `${agentSessionKey(sessionId)}/turns`;

/** Every {@link agentSessionsKey} starts with this, whichever team it is for. */
const SESSIONS_LIST_PREFIX = '/api/v1/agent-sessions?team_id=';

/**
 * Patch one session's row in every cached team list, without refetching.
 *
 * The sidebar rail reads the list cache, and nothing used to write to it — it only polled every
 * five seconds. So the thread could be streaming tool calls while the rail beside it still showed
 * the session's previous state, for whole seconds at a time. Every live source of session state
 * (the status channel, the agent socket) now mirrors what it knows into the list here.
 *
 * Matched by key prefix rather than by team id: the caller knows the session, not which team's
 * list happens to be cached, and patching a row that isn't there is a no-op anyway. The poll stays
 * the source of truth and corrects any drift.
 */
export function patchAgentSessionLists(sessionId: string, patch: Partial<AgentSession>): void {
  void mutate<AgentSession[]>(
    (key) => typeof key === 'string' && key.startsWith(SESSIONS_LIST_PREFIX),
    (prev) => prev?.map((s) => (s.id === sessionId ? { ...s, ...patch } : s)),
    { revalidate: false },
  );
}

export const MICRO_USD_PER_USD = 1_000_000;

/** Format micro-USD for display. Sub-cent spend still reads as a real number. */
export function formatMicroUsd(micro: number): string {
  const usd = micro / MICRO_USD_PER_USD;
  if (usd > 0 && usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}

export const agentSessionUsageKey = (sessionId: string) => `${agentSessionKey(sessionId)}/usage`;
export const agentSessionActivityKey = (sessionId: string) => `${agentSessionKey(sessionId)}/activity`;

/** Mirrors `db_client::models::ActivityKind`. */
export type AgentSessionActivityKind =
  | 'watch_started'
  | 'watch_stopped'
  | 'signal_detected'
  | 'session_woken'
  | 'external_commit'
  | 'paused'
  | 'resumed'
  | 'sandbox_error'
  | 'budget_exhausted'
  | 'plan_handoff';

/**
 * One entry in a session's activity log — something that happened *to* the
 * session rather than *in* it.
 *
 * A watched session spends most of its life asleep, being woken by the PR
 * watcher with nobody looking. These rows are the record of that, and they are
 * never shown to the model.
 */
export interface AgentSessionActivity {
  id: string;
  session_id: string;
  /** Per-session ordinal, and the pagination cursor. Two rows can share a millisecond. */
  seq: number;
  kind: AgentSessionActivityKind;
  /** Already humanised by the backend; render it as-is. */
  message: string;
  /** Shape varies by `kind`: `pr_number`, `html_url`, `sha`, `login`, … */
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/**
 * A session's spend against its budget.
 *
 * Recomputed server-side from the `llm_usage` ledger on every read (and the
 * running counter repaired if it drifted), so what's shown here is the same
 * figure a charge would be raised from. Polls while the session is live because
 * spend moves with every model call the agent makes, not with user actions.
 */
export function useAgentSessionUsage(sessionId: string | undefined, live = true) {
  const key = sessionId ? agentSessionUsageKey(sessionId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AgentSessionUsage>(key!), {
    refreshInterval: live ? 15000 : 0,
    revalidateOnFocus: true,
  });
  return { usage: data ?? null, isLoading, error, mutate };
}

/**
 * Set (`limitUsd`) or remove (`null`) a session's budget limit.
 *
 * Raising the limit above current spend also clears the exhausted state
 * server-side, so this doubles as "let it keep going".
 */
export async function setAgentSessionBudget(sessionId: string, limitUsd: number | null): Promise<AgentSessionUsage> {
  const usage = await fetchEndpointData<AgentSessionUsage>(`${agentSessionKey(sessionId)}/budget`, {
    method: 'PUT',
    body: { limit_usd: limitUsd },
  });
  await mutate(agentSessionUsageKey(sessionId), usage, { revalidate: false });
  // The limit and the exhausted marker live on the session row too.
  void mutate(agentSessionKey(sessionId));
  return usage;
}

/**
 * Set (`model`) or clear (`null`) which model a session runs on.
 *
 * The proxy resolves this per request, so the change takes effect on the
 * agent's next model call — no re-provision. Writes the returned row straight
 * into the detail cache so the picker reflects it immediately.
 */
export async function updateAgentSessionModel(sessionId: string, model: string | null): Promise<AgentSession> {
  const session = await fetchEndpointData<AgentSession>(`${agentSessionKey(sessionId)}/model`, {
    method: 'PUT',
    body: { model },
  });
  await mutate(agentSessionKey(sessionId), session, { revalidate: false });
  return session;
}

/** List a team's agent sessions (newest first). */
export function useAgentSessions(teamId: string | undefined) {
  const key = teamId ? agentSessionsKey(teamId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AgentSession[]>(key!), {
    refreshInterval: 5000,
  });
  return { sessions: data ?? [], isLoading, error, mutate };
}

/**
 * A single agent session.
 *
 * {@link useAgentSessionEvents} is the primary source of updates; this poll is
 * the fallback for when webhooks aren't delivered — local dev has no public URL,
 * and a guarded or dropped delivery is never re-derived.
 *
 * The `running` interval carries real weight: Daytona auto-stops the sandbox
 * after 5 minutes idle, and this GET is what runs the server-side liveness
 * reconcile that notices. It used to be 0, so an open tab sat on a stale
 * `running` indefinitely.
 */
export function useAgentSession(sessionId: string | undefined) {
  const key = sessionId ? agentSessionKey(sessionId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AgentSession>(key!), {
    refreshInterval: (latest) => {
      if (!latest) return 0;
      if (latest.status === 'provisioning') return 3000;
      if (latest.status === 'running') return 30000;
      // A planning session has no sandbox to go stale underneath us, and no webhook to miss —
      // its row only changes when the user acts. Focus revalidation covers the rest.
      return 0; // planning/hibernated/error only move on user intent or a pushed event
    },
    // Overrides the global `false`: returning to the tab after a few idle
    // minutes is exactly when the sandbox has been stopped underneath us.
    revalidateOnFocus: true,
  });
  return { session: data ?? null, isLoading, error, mutate };
}

/**
 * Lifecycle frame published to `sandbox:events:agent-session:{id}` and forwarded
 * verbatim by `GET /:id/events`. A strict subset of {@link AgentSession}.
 */
export interface AgentSessionStatusEvent {
  type: 'agent_session_status';
  session_id: string;
  repository_id: string;
  sandbox_id: string | null;
  status: AgentSessionStatus;
  sandbox_status: SandboxStatus;
  provision_step: ProvisionStep | null;
  error_message: string | null;
  /** When the agent stopped on an `ask_user` question; `null` when it is working or idle. */
  awaiting_user_input_at: string | null;
  /** Mirrors {@link AgentSession.last_active_at} — the agent's own liveness, pushed. */
  last_active_at: string | null;
  timestamp: string;
}

/**
 * An appended activity row, pushed down the same channel as the status frames.
 *
 * Sharing the channel is deliberate on the backend — the socket forwards payloads
 * verbatim, so this needed no transport change — which is why the handler below
 * has to switch on `type` rather than assuming one shape.
 */
export interface AgentSessionActivityEvent {
  type: 'agent_session_activity';
  session_id: string;
  id: string;
  seq: number;
  kind: AgentSessionActivityKind;
  message: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export type AgentSessionEvent = AgentSessionStatusEvent | AgentSessionActivityEvent;

/**
 * Subscribe to a session's lifecycle events and fold them into the
 * {@link useAgentSession} cache.
 *
 * Safe to hold open in every state — unlike the agent bridge and the files/pty
 * routes, this endpoint only does an auth lookup plus a Redis subscribe, so it
 * never resumes the sandbox. Staying subscribed while hibernated is the point:
 * it's how an auto-stop, or a resume started from another tab, reaches this one.
 */
export function useAgentSessionEvents(sessionId: string | undefined) {
  useEffect(() => {
    if (!sessionId) return;
    const key = agentSessionKey(sessionId);
    // The last status this socket reported, so the settle-revalidates below fire on a *transition*
    // rather than on every frame. They stopped being the same thing when the agent's liveness
    // stamp joined this channel: a running agent now publishes every ~10s, and each of those
    // frames still says `running`.
    let lastStatus: AgentSessionStatus | null = null;

    const conn = new WebSocketConnection({
      url: () => wsUrl(`${key}/events`),
      onMessage: (e) => {
        let evt: AgentSessionEvent | null = null;
        try {
          evt = JSON.parse(e.data) as AgentSessionEvent;
        } catch {
          return;
        }
        if (!evt || evt.session_id !== sessionId) return;

        if (evt.type === 'agent_session_activity') {
          const activity: AgentSessionActivity = {
            id: evt.id,
            session_id: evt.session_id,
            seq: evt.seq,
            kind: evt.kind,
            message: evt.message,
            metadata: evt.metadata,
            created_at: evt.created_at,
          };
          // Append rather than revalidate: rows are immutable and arrive in
          // order, so the frame *is* the update. The `prev` guard keeps an event
          // that beats the first GET from minting a log with a hole in it.
          mutate<AgentSessionActivity[]>(
            agentSessionActivityKey(sessionId),
            (prev) => {
              if (!prev) return prev;
              if (prev.some((row) => row.id === activity.id)) return prev;
              return [...prev, activity];
            },
            { revalidate: false },
          );
          // An autonomous wake changes `pr_watch_enabled` and the session's
          // status underneath us, and neither travels on this frame.
          if (evt.kind === 'watch_stopped' || evt.kind === 'external_commit' || evt.kind === 'session_woken') {
            void mutate(key);
          }
          return;
        }

        if (evt.type !== 'agent_session_status') return;
        const { status, sandbox_id, sandbox_status, provision_step, error_message } = evt;
        const { awaiting_user_input_at, last_active_at } = evt;

        const patch = {
          status,
          sandbox_id,
          sandbox_status,
          provision_step,
          sandbox_error_message: error_message,
          awaiting_user_input_at,
          last_active_at,
        };

        // Merge rather than revalidate: provisioning emits a frame per step, and
        // each revalidate would drag a full Daytona round-trip through the
        // server's liveness reconcile. The `prev` guard matters — the frame is
        // only the status fields, so merging into an empty cache (deep link, or
        // an event beating the first GET) would mint a partial row.
        mutate<AgentSession>(key, (prev) => (prev ? { ...prev, ...patch } : prev), { revalidate: false });
        // The same fields drive the sidebar rail, which reads the list cache.
        patchAgentSessionLists(sessionId, patch);

        const changed = status !== lastStatus;
        lastStatus = status;
        // Both of these are round-trips — the session GET drags Daytona through the server's
        // liveness reconcile — so they must stay tied to the transition. Repeating them per
        // liveness frame would poll Daytona every 10s for the whole of every run.
        if (!changed) return;
        if (status === 'running' || status === 'hibernated' || status === 'error') {
          void mutate(key); // settled — pick up fields the frame doesn't carry
        }
        if (status === 'running') {
          // The agent (re)started; durable history may have grown while the
          // chat socket was closed.
          void mutate(agentSessionTurnsKey(sessionId));
        }
      },
      // The channel carries deltas only and sends no snapshot on connect, so
      // anything missed while disconnected is unrecoverable without this.
      onReconnect: () => {
        void mutate(key);
        // Deltas only, no snapshot on connect — so anything appended while the
        // socket was down is unrecoverable without refetching the log.
        void mutate(agentSessionActivityKey(sessionId));
      },
    });

    return () => {
      conn.dispose();
    };
  }, [sessionId]);
}

/**
 * A session's activity log.
 *
 * No polling: rows arrive over {@link useAgentSessionEvents}, and that socket is
 * safe to hold open while the session is hibernated — which is exactly when the
 * interesting rows get written.
 */
export function useAgentSessionActivity(sessionId: string | undefined) {
  const key = sessionId ? agentSessionActivityKey(sessionId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AgentSessionActivity[]>(key!));
  return { activity: data ?? null, isLoading, error, mutate };
}

/**
 * Turn PR watching on or off for a session.
 *
 * Turning it back on is the documented way out of every automatic stop, so the
 * backend also clears the stop reason, resets the wake counters and adopts the
 * PR's current head — see `routes/agent_sessions/activity.rs`.
 */
export async function setPrWatch(sessionId: string, enabled: boolean): Promise<void> {
  await fetchEndpointData(`${agentSessionKey(sessionId)}/pr-watch`, {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
  await mutate(agentSessionKey(sessionId));
  await mutate(agentSessionActivityKey(sessionId));
}

export interface AgentSessionHistoryToolCall {
  id: string;
  name: string;
  input: unknown;
  output: string | null;
  /** Pictures the call made available: `data:` URLs from `browser_screenshot`, remote ones from
   *  `web_fetch`. Absent when the call produced none. */
  imageUrls?: string[];
  /** Whether the call failed — for `set_todos`, that the agent loop refused the list. Absent when
   *  it succeeded. */
  isError?: boolean;
}

/** Mirrors the backend wire `HistoryTurn` (see `routes/agent_sessions/turns.rs`). */
export interface AgentSessionHistoryTurn {
  role: 'user' | 'assistant';
  content: string | null;
  /** Base64 data URLs attached to a user turn. */
  images?: string[];
  toolCalls?: AgentSessionHistoryToolCall[];
  /** `job` marks a background job's report: written by the agent loop, never typed by the user. */
  origin?: 'job';
}

/**
 * Durable chat history straight from the DB (`agent_session_turn`) — no live
 * sandbox required, so the thread renders on reload even while the session is
 * hibernated. The live WebSocket ({@link useAgentSessionSocket}) takes over once
 * the sandbox is running.
 */
export function useAgentSessionTurns(sessionId: string | undefined) {
  const key = sessionId ? agentSessionTurnsKey(sessionId) : null;
  const { data, error, isLoading, mutate } = useSWR(key, () => fetchEndpointData<AgentSessionHistoryTurn[]>(key!));
  return { turns: data ?? null, isLoading, error, mutate };
}

export interface CreateAgentSessionBody {
  prompt?: string;
  repository_id: string;
  branch?: string;
  tracker_issue_id?: string;
  snapshot_override?: string;
  /** Client alias of the Fireworks model to run the session on; omit for the default. */
  model?: string;
  /** Base64 data URLs attached to `prompt`; delivered with it to the agent. */
  images?: string[];
  /**
   * `'plan'` starts a planning session, which provisions no sandbox. Omit (or `'code'`) for a
   * normal coding session.
   */
  mode?: AgentSessionMode;
}

export async function createAgentSession(body: CreateAgentSessionBody): Promise<AgentSession> {
  const session = await fetchEndpointData<AgentSession>('/api/v1/agent-sessions', { method: 'POST', body });
  // Prime the detail cache so navigating straight there paints the thread — and
  // the user's own prompt — on the first frame, with no loading state.
  await mutate(agentSessionKey(session.id), session, { revalidate: false });
  void mutate(agentSessionsKey(session.team_id));
  return session;
}

export async function deleteAgentSession(sessionId: string): Promise<void> {
  await fetchEndpointData(`/api/v1/agent-sessions/${sessionId}`, { method: 'DELETE' });
}

/**
 * Archive or restore a session. Archiving keeps the thread and its history but
 * stops the sandbox immediately, and locks it down: the server refuses resume and
 * prompt for an archived session, so it cannot be woken by an open tab or a
 * queued message. Restoring releases the lock without starting anything — the
 * session comes back on the next resume, from a cold start.
 */
export async function archiveAgentSession(sessionId: string, archived: boolean): Promise<AgentSession> {
  const session = await fetchEndpointData<AgentSession>(`${agentSessionKey(sessionId)}/archive`, {
    method: 'POST',
    body: { archived },
  });
  await mutate(agentSessionKey(sessionId), session, { revalidate: false });
  return session;
}

/**
 * Resume a hibernated session's sandbox (Daytona auto-stops idle ones after five
 * minutes). Returns as soon as the server accepts; progress arrives over
 * {@link useAgentSessionEvents}.
 */
export async function resumeAgentSession(sessionId: string): Promise<void> {
  const key = agentSessionKey(sessionId);
  // The server flips to provisioning before it answers, so reflect that now and
  // let the status line move on click rather than on the next poll.
  await mutate<AgentSession>(
    key,
    (prev) =>
      prev ? { ...prev, status: 'provisioning', sandbox_status: 'provisioning', provision_step: 'resume_start' } : prev,
    { revalidate: false },
  );
  try {
    await fetchEndpointData(`${key}/resume`, { method: 'POST' });
  } catch (err) {
    void mutate(key); // undo the optimistic flip
    throw err;
  }
}

/**
 * REST fallback for sending a prompt when the agent WebSocket isn't open — which
 * is any time the sandbox isn't running, since opening that socket resumes the
 * sandbox and so stays gated on `running`.
 *
 * The endpoint resumes a paused sandbox transparently, so this doubles as
 * "resume on user intent". It legitimately blocks while a cold start finishes
 * (the server polls for up to two minutes), hence the generous timeout.
 */
export async function promptAgentSession(sessionId: string, message: string, images?: string[]): Promise<void> {
  await fetchEndpointData(`${agentSessionKey(sessionId)}/prompt`, {
    method: 'POST',
    body: images && images.length > 0 ? { message, images } : { message },
    signal: AbortSignal.timeout(150_000),
  });
}

/** One question's answer. Empty on both counts means "you decide". */
export interface AgentSessionAnswer {
  selected: string[];
  text: string | null;
}

/**
 * How the user responded to a question card: question by question, or one message in their own
 * words that answers the whole card — what typing into the composer while a card is open sends.
 */
export type AgentSessionAskResponse = { answers: AgentSessionAnswer[] } | { reply: string };

/**
 * Answer the `ask_user` question a session stopped on, and resume the turn it ended.
 *
 * REST rather than the agent socket on purpose: that socket only opens for a running sandbox, and
 * a question routinely outlives its sandbox — answering has to work on a session hibernated a
 * week ago. Like `promptAgentSession` this resumes the sandbox on the way through, so it can
 * legitimately block behind a cold start.
 *
 * `answers` is positional against the question list, and the server rejects a `toolCallId` that
 * isn't the one still pending — which is what makes a second tab's answer, or a click on a card
 * that has since been superseded, a clean error rather than a duplicate turn.
 *
 * Resolves to the result the server stored for the call, so the card can be retired with exactly
 * what a later reload reads back.
 */
export async function answerAgentSession(
  sessionId: string,
  toolCallId: string,
  response: AgentSessionAskResponse,
): Promise<string> {
  const { output } = await fetchEndpointData<{ output: string }>(`${agentSessionKey(sessionId)}/answer`, {
    method: 'POST',
    body: { toolCallId, ...response },
    signal: AbortSignal.timeout(150_000),
  });
  return output;
}
